import type { OAuthProvider } from "@lorevok/domain";
import { supabase } from "./supabaseClient.js";
import { signInWithUsername } from "../apiClient.js";
import { LEGAL_EFFECTIVE_DATE } from "./siteInfo.js";

/**
 * Signs in with an email or a username + password. Throws with a readable
 * message on failure. Anything with an "@" is an email and goes to Supabase
 * directly; otherwise it is a username, which the API resolves and signs in
 * with (signInWithUsername), handing back a session for the auth client.
 */
export async function signInWithPassword(login: string, password: string): Promise<void> {
  if (!supabase) throw new Error("Auth is not configured.");
  const identifier = login.trim();
  if (!identifier.includes("@")) {
    const tokens = await signInWithUsername(identifier, password);
    const { error } = await supabase.auth.setSession({
      access_token: tokens.accessToken,
      refresh_token: tokens.refreshToken
    });
    if (error) throw new Error(error.message);
    return;
  }
  const { error } = await supabase.auth.signInWithPassword({ email: identifier, password });
  if (error) throw new Error(error.message);
}

/**
 * Registers a new account. The caller has already collected the age (18+) and
 * Terms/Privacy consent; it is recorded on the account's user metadata — with the
 * version (effective date) of the documents accepted — as evidence of consent.
 * The chosen username rides along in the metadata too: the database's
 * handle_new_user trigger (migration 0038) stores it on the profile, and fails
 * the sign-up when another account took it in the meantime.
 * Returns needsConfirmation=true when the project requires email confirmation
 * (no session is returned until the link is clicked).
 *
 * existingAccount=true when the email already belongs to a confirmed account.
 * With email confirmation on, Supabase does not say so (that would reveal who
 * is registered): it answers with a stand-in user that has no identities. The
 * page still shows "check your email", so it reveals nothing either; only
 * analytics uses the flag, so a repeat is not counted as a sign-up.
 */
export async function signUpWithPassword(
  email: string,
  password: string,
  username: string,
  now: Date = new Date()
): Promise<{ needsConfirmation: boolean; existingAccount: boolean }> {
  if (!supabase) throw new Error("Auth is not configured.");
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: typeof window !== "undefined" ? `${window.location.origin}/signin` : undefined,
      data: {
        username,
        age_confirmed_18_plus: true,
        terms_accepted_at: now.toISOString(),
        terms_version: LEGAL_EFFECTIVE_DATE
      }
    }
  });
  if (error) throw new Error(error.message);
  return {
    needsConfirmation: !data.session,
    existingAccount: !data.session && data.user?.identities?.length === 0
  };
}

/**
 * Leaves for Google's or Discord's sign-in page (Supabase Auth's OAuth). The
 * provider sends the visitor back through Supabase to /signin with the session
 * in the address (#access_token=…, the client's implicit flow), which the auth
 * client picks up on load (detectSessionInUrl) — the same landing as an email
 * confirmation link. A first sign-in creates the account; it has no username
 * yet, so the app asks for one ("Finish signing up") before anything else.
 * Resolves only if the page did not navigate away; throws on failure.
 */
export async function signInWithOAuthProvider(provider: OAuthProvider): Promise<void> {
  if (!supabase) throw new Error("Auth is not configured.");
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/signin`,
      // Google otherwise signs straight in with whichever account the browser
      // is using; let the visitor pick (Discord shows its account on its page).
      ...(provider === "google" ? { queryParams: { prompt: "select_account" } } : {})
    }
  });
  if (error) throw new Error(error.message);
}

/**
 * Records the 18+ and Terms/Privacy consent on the signed-in account's user
 * metadata — what signUpWithPassword stores at sign-up — for an account made
 * with Google or Discord, which ticks them on "Finish signing up" instead.
 */
export async function recordSignUpConsent(now: Date = new Date()): Promise<void> {
  if (!supabase) throw new Error("Auth is not configured.");
  const { error } = await supabase.auth.updateUser({
    data: {
      age_confirmed_18_plus: true,
      terms_accepted_at: now.toISOString(),
      terms_version: LEGAL_EFFECTIVE_DATE
    }
  });
  if (error) throw new Error(error.message);
}

/**
 * Re-sends the sign-up confirmation link to an account that has not been
 * confirmed yet (open-signup mode). The link returns to /signin, like the first.
 */
export async function resendSignupConfirmation(email: string): Promise<void> {
  if (!supabase) throw new Error("Auth is not configured.");
  const { error } = await supabase.auth.resend({
    type: "signup",
    email,
    options: { emailRedirectTo: typeof window !== "undefined" ? `${window.location.origin}/signin` : undefined }
  });
  if (error) throw new Error(error.message);
}

/**
 * Emails a password-reset link (self-serve reset, open-signup mode). The link
 * returns to /signin, where the PASSWORD_RECOVERY session lets the user set a
 * new password. Resolves even for unknown emails (Supabase does not reveal them).
 */
export async function sendPasswordResetEmail(email: string): Promise<void> {
  if (!supabase) throw new Error("Auth is not configured.");
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: typeof window !== "undefined" ? `${window.location.origin}/signin` : undefined
  });
  if (error) throw new Error(error.message);
}

/** Sets a new password for the signed-in (or password-recovery) session. */
export async function updatePassword(password: string): Promise<void> {
  if (!supabase) throw new Error("Auth is not configured.");
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw new Error(error.message);
}

/**
 * Subscribes to the password-recovery event (the user arrived from a reset
 * link). Returns an unsubscribe function; a no-op when auth isn't configured.
 */
export function onPasswordRecovery(handler: () => void): () => void {
  if (!supabase) return () => undefined;
  const { data } = supabase.auth.onAuthStateChange((event) => {
    if (event === "PASSWORD_RECOVERY") handler();
  });
  return () => data.subscription.unsubscribe();
}

export async function signOut(): Promise<void> {
  if (!supabase) return;
  await supabase.auth.signOut();
}
